import { BitField } from "./bitField";

export type ChannelPermissionsString = keyof typeof ChannelPermissions.FLAGS;
export type UserPermissionsString = keyof typeof UserPermissions.FLAGS;
export type ServerPermissionsString = keyof typeof ServerPermissions.FLAGS;
export type ChannelPermissionsResolvable =
  | number
  | ChannelPermissionsString
  | ChannelPermissions
  | ChannelPermissionsResolvable[];
export type UserPermissionsResolvable =
  | number
  | UserPermissionsString
  | UserPermissions
  | UserPermissionsResolvable[];
export type ServerPermissionsResolvable =
  | number
  | ServerPermissionsString
  | ServerPermissions
  | ServerPermissionsResolvable[];

export declare interface ServerPermissions {
  serialize(): Record<ServerPermissionsString, boolean>;
  any(bit: ServerPermissionsResolvable): boolean;
  add(...bits: ServerPermissionsResolvable[]): this;
  remove(...bits: ServerPermissionsResolvable[]): this;
  has(bit: ServerPermissionsResolvable): boolean;
}

export declare interface ChannelPermissions {
  serialize(): Record<ChannelPermissionsString, boolean>;
  any(bit: ChannelPermissionsResolvable): boolean;
  add(...bits: ChannelPermissionsResolvable[]): this;
  remove(...bits: ChannelPermissionsResolvable[]): this;
  has(bit: ChannelPermissionsResolvable): boolean;
}

export declare interface UserPermissions {
  serialize(): Record<UserPermissionsString, boolean>;
  any(bit: UserPermissionsResolvable): boolean;
  add(...bits: UserPermissionsResolvable[]): this;
  remove(...bits: UserPermissionsResolvable[]): this;
  has(bit: UserPermissionsResolvable): boolean;
}

export class ChannelPermissions extends BitField {
  static readonly FLAGS = {
    VIEW_CHANNEL: 1 ** 0,
    SEND_MESSAGE: 1 ** 22,
    MANAGE_MESSAGE: 1 ** 2,
    MANAGE_CHANNEL: 1 ** 3,
    VOICE_CALL: 1 ** 4,
    INVITE_OTHERS: 1 ** 5,
    EMBED_LINKS: 1 ** 6,
    UPLOAD_FILES: 1 ** 7,
  } as const;

  constructor(bits?: ChannelPermissionsResolvable) {
    super(bits);
  }

  static resolve(bit: ChannelPermissionsResolvable): number {
    return super.resolve(bit);
  }
}

export class UserPermissions extends BitField {
  static readonly FLAGS = {
    ACCESS: 1 ** 0,
    VIEW_PROFILE: 1 ** 1,
    SEND_MESSAGES: 1 ** 2,
    INVITE: 1 ** 3,
  } as const;

  constructor(bits?: UserPermissionsResolvable) {
    super(bits);
  }

  static resolve(bit: UserPermissionsResolvable): number {
    return super.resolve(bit);
  }
}

export class ServerPermissions extends BitField {
  static readonly FLAGS = {
    VIEW_SERVER: 1 ** 0,
    MANAGE_ROLES: 1 ** 1,
    MANAGE_CHANNELS: 1 ** 2,
    MANAGE_SERVER: 1 ** 3,
    KICK_MEMBERS: 1 ** 4,
    BAN_MEMBERS: 1 ** 5,
    CHANGE_NICKNAME: 1 ** 12,
    MANAGE_NICKNAMES: 1 ** 13,
    CHANGE_AVATAR: 1 ** 14,
    REMOVE_AVATARS: 1 ** 15,
  } as const;

  constructor(bits?: ServerPermissionsResolvable) {
    super(bits);
  }

  static resolve(bit: ServerPermissionsResolvable): number {
    return super.resolve(bit);
  }
}

export class FullPermissions extends BitField {
  static readonly FLAGS = {
    // * Generic permissions
    /// Manage the channel or channels on the server
    ManageChannel: 2 ** 0,
    /// Manage the server
    ManageServer: 2 ** 1,
    /// Manage permissions on servers or channels
    ManagePermissions: 2 ** 2,
    /// Manage roles on server
    ManageRole: 2 ** 3,
    /// Manage server customisation (includes emoji)
    ManageCustomisation: 2 ** 4,

    // * Member permissions
    /// Kick other members below their ranking
    KickMembers: 2 ** 6,
    /// Ban other members below their ranking
    BanMembers: 2 ** 7,
    /// Timeout other members below their ranking
    TimeoutMembers: 2 ** 8,
    /// Assign roles to members below their ranking
    AssignRoles: 2 ** 9,
    /// Change own nickname
    ChangeNickname: 2 ** 10,
    /// Change or remove other's nicknames below their ranking
    ManageNicknames: 2 ** 11,
    /// Change own avatar
    ChangeAvatar: 2 ** 12,
    /// Remove other's avatars below their ranking
    RemoveAvatars: 2 ** 13,

    // * Channel permissions
    /// View a channel
    ViewChannel: 2 ** 20,
    /// Read a channel's past message history
    ReadMessageHistory: 2 ** 21,
    /// Send a message in a channel
    SendMessage: 2 ** 22,
    /// Delete messages in a channel
    ManageMessages: 2 ** 23,
    /// Manage webhook entries on a channel
    ManageWebhooks: 2 ** 24,
    /// Create invites to this channel
    InviteOthers: 2 ** 25,
    /// Send embedded content in this channel
    SendEmbeds: 2 ** 26,
    /// Send attachments and media in this channel
    UploadFiles: 2 ** 27,
    /// Masquerade messages using custom nickname and avatar
    Masquerade: 2 ** 28,
    /// React to messages with emoji
    React: 2 ** 29,

    // * Voice permissions
    /// Connect to a voice channel
    Connect: 2 ** 30,
    /// Speak in a voice call
    Speak: 2 ** 31,
    /// Share video in a voice call
    Video: 2 ** 32,
    /// Mute other members with lower ranking in a voice call
    MuteMembers: 2 ** 33,
    /// Deafen other members with lower ranking in a voice call
    DeafenMembers: 2 ** 34,
    /// Move members between voice channels
    MoveMembers: 2 ** 35,

    // * Mention permissions
    /// Mention @everyone or @online
    MentionEveryone: 2 ** 37,
    /// Mention a role
    MentionRoles: 2 ** 38,

    // * Grant all permissions
    /// Safely grant all permissions
    GrantAll: 0x000f_ffff_ffff_ffff,
  } as const;

  constructor(
    bits?:
      | number
      | string
      | FullPermissions
      | Array<number | string | FullPermissions>,
  ) {
    super(bits);
  }

  static resolve(bit: number | string | FullPermissions): number {
    return super.resolve(bit);
  }
}

export const DEFAULT_PERMISSION_DM = new ChannelPermissions([
  "VIEW_CHANNEL",
  "VIEW_CHANNEL",
  "MANAGE_CHANNEL",
  "VOICE_CALL",
  "EMBED_LINKS",
  "UPLOAD_FILES",
]).freeze();
